/**
 * usePhoneVerification Hook
 *
 * Live Testing Preview에서 사용하는 전화번호 인증 상태 관리
 * 실제 SMS 인증이 아닌 Mock 인증으로 UI 테스트 용도
 */

import { useState, useCallback, useEffect } from 'react';

interface UsePhoneVerificationProps {
  /** Tree가 변경되면 인증 상태 초기화를 위한 키 */
  treeKey?: string;
}

interface UsePhoneVerificationReturn {
  /** 특정 질문의 인증 완료 여부 확인 */
  isVerified: (questionKey: string) => boolean;
  /** 특정 질문의 인증 중 여부 확인 */
  isVerifying: (questionKey: string) => boolean;
  /** 인증 시작 (Mock - 1초 후 완료 처리) */
  handleVerify: (questionKey: string) => void;
  /** 특정 질문의 인증 상태 초기화 */
  resetVerification: (questionKey: string) => void;
  /** 모든 인증 상태 초기화 */
  resetAllVerifications: () => void;
}

/**
 * Live Testing용 전화번호 인증 상태 관리 훅
 *
 * 실제 SMS 인증이 아닌 Mock 인증을 제공합니다.
 * Verify 버튼 클릭 시 1초 후 인증 완료로 처리됩니다.
 *
 * @example
 * ```tsx
 * const { isVerified, isVerifying, handleVerify } = usePhoneVerification({
 *   treeKey: selectedTreeKey,
 * });
 *
 * <V2QsPhoneNumber
 *   showVerification
 *   isVerified={isVerified('phone-question')}
 *   isVerifying={isVerifying('phone-question')}
 *   onVerifyClick={() => handleVerify('phone-question')}
 * />
 * ```
 */
export function usePhoneVerification({
  treeKey,
}: UsePhoneVerificationProps = {}): UsePhoneVerificationReturn {
  // 인증 완료된 질문들 (questionKey Set)
  const [verifiedSet, setVerifiedSet] = useState<Set<string>>(new Set());
  // 인증 중인 질문들 (questionKey Set)
  const [verifyingSet, setVerifyingSet] = useState<Set<string>>(new Set());

  // Tree가 변경되면 모든 인증 상태 초기화
  useEffect(() => {
    setVerifiedSet(new Set());
    setVerifyingSet(new Set());
  }, [treeKey]);

  // 특정 질문의 인증 완료 여부 확인
  const isVerified = useCallback(
    (questionKey: string): boolean => {
      return verifiedSet.has(questionKey);
    },
    [verifiedSet]
  );

  // 특정 질문의 인증 중 여부 확인
  const isVerifying = useCallback(
    (questionKey: string): boolean => {
      return verifyingSet.has(questionKey);
    },
    [verifyingSet]
  );

  // 인증 시작 (Mock - 1초 후 완료 처리)
  const handleVerify = useCallback((questionKey: string) => {
    // 이미 인증 중이거나 완료된 경우 무시
    if (verifyingSet.has(questionKey) || verifiedSet.has(questionKey)) {
      return;
    }

    // 인증 중 상태로 설정
    setVerifyingSet((prev) => new Set(prev).add(questionKey));

    // 1초 후 인증 완료 처리 (Mock)
    setTimeout(() => {
      setVerifyingSet((prev) => {
        const next = new Set(prev);
        next.delete(questionKey);
        return next;
      });
      setVerifiedSet((prev) => new Set(prev).add(questionKey));
    }, 1000);
  }, [verifyingSet, verifiedSet]);

  // 특정 질문의 인증 상태 초기화
  const resetVerification = useCallback((questionKey: string) => {
    setVerifiedSet((prev) => {
      const next = new Set(prev);
      next.delete(questionKey);
      return next;
    });
    setVerifyingSet((prev) => {
      const next = new Set(prev);
      next.delete(questionKey);
      return next;
    });
  }, []);

  // 모든 인증 상태 초기화
  const resetAllVerifications = useCallback(() => {
    setVerifiedSet(new Set());
    setVerifyingSet(new Set());
  }, []);

  return {
    isVerified,
    isVerifying,
    handleVerify,
    resetVerification,
    resetAllVerifications,
  };
}
